/**
 * Render proxy descriptions (architecture §6.1): what the renderer draws,
 * described without reference to the world.
 *
 * Translators in the editor turn components into these, and the render backend
 * turns them into GPU objects. It mirrors Unreal's split between a primitive
 * component and its scene proxy. `render/` never sees entities, so any backend
 * can be written against these types alone.
 *
 * Colors stay as ColorValue (an sRGB triple or a theme token), so the backend
 * resolves theme tokens itself and a theme switch re-colors the scene without
 * re-translating anything.
 */
import type { Rgb } from "../core/color";
import type { Aabb } from "../core/math/aabb";
import type { Mat4 } from "../core/math/mat4";
import type { Vec3 } from "../core/math/vec3";

/** A color as authored: sRGB 0..1, or a `viewport.*` theme token like `keyLightTint`. */
export type ProxyColor = Rgb | { readonly theme: string };

/** Opaque handle to a proxy owned by the backend. */
export type ProxyHandle = number & { readonly __brand: "ProxyHandle" };

export type MeshPrimitive = "platform" | "box" | "cylinder" | "sphere" | "plane" | "capsule";

export type MeshMaterial = "platform" | "standard" | "placeholder";

export interface MeshProxyDesc {
  readonly kind: "mesh";
  /** Built-in geometry; null draws none (a model not yet loaded, or no mesh at all). */
  readonly primitive: MeshPrimitive | null;
  /** Content-relative glTF path, drawn once loaded (SPEC §7). */
  readonly asset: string | null;
  /**
   * Size in meters: width (x), height (y), depth (z). The pivot is at the base
   * center (geometry spans y ∈ [0, height]), like Unreal characters and props,
   * so a figure placed at a platform's top height stands on it.
   */
  readonly size: Vec3;
  readonly material: MeshMaterial;
  readonly color: ProxyColor;
  readonly castShadow: boolean;
  /** Plays one of the model's clips (the Animator component); null leaves the model in its bind pose. */
  readonly animation: MeshAnimationDesc | null;
  /**
   * The model streams at low network priority and the scene doesn't wait for
   * it (MeshRenderer › Load in background): a large environment.
   */
  readonly background: boolean;
}

/** Mirrors Unreal's "Use Animation Asset" mode: one clip, looping or once, at a play rate. */
export interface MeshAnimationDesc {
  /** A clip name in the model's glTF; "" plays the first clip. */
  readonly clip: string;
  readonly playing: boolean;
  readonly loop: boolean;
  /** Play rate: 1 is authored speed. */
  readonly speed: number;
}

export interface LightProxyDesc {
  readonly kind: "light";
  readonly light: "directional" | "point" | "spot";
  readonly color: ProxyColor;
  readonly intensity: number;
  /** Meters. Point and spot only. */
  readonly range: number;
  /** Cone angle in degrees. Spot only. */
  readonly angle: number;
  readonly castShadow: boolean;
}

export interface VfxProxyDesc {
  readonly kind: "vfx";
  readonly effect: "emberRing";
  readonly radius: number;
  readonly count: number;
  readonly color: ProxyColor;
}

/** One line of text on an in-world panel. `placeholder` renders as secondary text (rule 9). */
export interface WorldUiLine {
  readonly text: string;
  readonly style: "eyebrow" | "title" | "meta" | "body" | "placeholder";
  /** An item of a list (a Text bound to one, e.g. a Role's highlights), drawn as a bullet. */
  readonly bullet?: boolean;
}

/** An image in a UI panel: a texture asset, as wide as the panel's inner width allows, this tall. */
export interface WorldUiImage {
  readonly image: string;
  /** Height in panel pixels (WORLD_UI_PIXELS_PER_METER). */
  readonly height: number;
}

export type WorldUiItem = WorldUiLine | WorldUiImage;

export interface WorldUiProxyDesc {
  readonly kind: "worldUI";
  readonly width: number;
  readonly height: number;
  /** The panel look (background and border), or bare text and images. */
  readonly background: boolean;
  /** Stacked top to bottom (worldUiLayout). */
  readonly items: readonly WorldUiItem[];
}

/**
 * A camera entity, as the editor draws it: a see-through camera body and its
 * view frustum (Unity's camera gizmo, Unreal's camera mesh). Editor-only, like
 * the gizmo: Game View and camera previews leave it out.
 */
export interface CameraProxyDesc {
  readonly kind: "camera";
  /** Vertical field of view in degrees, for the frustum. */
  readonly fov: number;
}

export type ProxyDesc =
  MeshProxyDesc | LightProxyDesc | VfxProxyDesc | WorldUiProxyDesc | CameraProxyDesc;

/**
 * The camera gizmo's body, in the camera's space (meters; it looks down -Z):
 * a box behind the pivot and a lens hood in front. Framing a camera frames this.
 */
export const CAMERA_BODY_BOUNDS: Aabb = { min: [-0.12, -0.1, -0.14], max: [0.12, 0.1, 0.28] };

/** What the viewport shows around the geometry, from WorldSettings. */
export interface EnvironmentDesc {
  readonly preset: "studio-dusk";
  /** The studio floor; off when the level brings its own ground. */
  readonly floor: boolean;
}

/** The level's fog (the Fog component); the renderer applies Day or Night by the theme's mode. */
export interface FogDesc {
  /** Exponential-squared density per meter, by variant. */
  readonly dayDensity: number;
  readonly nightDensity: number;
  /** Fades toward the Sky's haze rather than the studio horizon. */
  readonly colorFromSky: boolean;
}

/** The level's sky (the Sky component); the renderer shows Day or Night by the theme's mode. */
export interface SkyDesc {
  /** Content paths of equirectangular panoramas; null shows the studio gradient for that mode. */
  readonly day: string | null;
  readonly night: string | null;
  /** Degrees about the vertical. */
  readonly rotation: number;
}

export interface PostProcessDesc {
  readonly exposure: number;
  readonly bloomStrength: number;
  readonly bloomThreshold: number;
  readonly tonemapper: "AgX" | "ACES";
}

/** The viewport camera for one frame. The view matrix maps world → camera space. */
export interface CameraDesc {
  readonly view: Mat4;
  readonly position: Vec3;
  /** Vertical field of view in degrees (perspective views). */
  readonly fov: number;
  /** Meters shown top to bottom in an orthographic view; null for perspective. */
  readonly orthographicHeight: number | null;
  readonly near: number;
  readonly far: number;
}

export interface ShowFlags {
  readonly grid: boolean;
  readonly bounds: boolean;
  readonly wireframe: boolean;
  /** Camera gizmos (bodies, and selected cameras' frustums). */
  readonly cameras: boolean;
  /** Every camera's frustum, not only selected ones'. */
  readonly allFrustums: boolean;
}

export type ViewMode = "lit" | "unlit" | "wireframe";

export type GizmoMode = "translate" | "rotate" | "scale";

/** Snap increments: meters, degrees, and scale units. */
export interface GizmoSnap {
  readonly translate: number;
  readonly rotate: number;
  readonly scale: number;
}

/**
 * The transform gizmo. It sits at a world pose rather than on a proxy, so
 * entities with nothing to draw (a Zone, a Camera) can be moved too.
 */
export interface GizmoDesc {
  readonly world: Mat4;
  readonly mode: GizmoMode;
  readonly snap: GizmoSnap | null;
}

/** A gizmo drag, reported as the dragged pose in world space. */
export interface GizmoEvent {
  readonly phase: "start" | "change" | "end";
  readonly world: Mat4;
}

export const DEFAULT_POST_PROCESS: PostProcessDesc = {
  exposure: 1,
  bloomStrength: 0.6,
  bloomThreshold: 0.8,
  tonemapper: "AgX",
};

export const DEFAULT_ENVIRONMENT: EnvironmentDesc = {
  preset: "studio-dusk",
  floor: true,
};

/** How high an ember ring's particles rise, in meters. Shared by bounds and the renderer. */
export const EMBER_RISE = 1.4;

/**
 * A proxy's bounds in its own space, used for F-framing and the selection
 * bracket. Lights have none: framing a light should frame what it lights.
 */
export function localBounds(desc: ProxyDesc): Aabb | null {
  switch (desc.kind) {
    case "mesh": {
      // A model's bounds are known once it's drawn (RenderBackend.proxyBounds).
      if (desc.primitive === null) return null;
      const [w, h, d] = desc.size;
      return { min: [-w / 2, 0, -d / 2], max: [w / 2, h, d / 2] };
    }
    case "worldUI":
      return {
        min: [-desc.width / 2, -desc.height / 2, 0],
        max: [desc.width / 2, desc.height / 2, 0],
      };
    case "vfx":
      return { min: [-desc.radius, 0, -desc.radius], max: [desc.radius, EMBER_RISE, desc.radius] };
    case "camera":
      return CAMERA_BODY_BOUNDS;
    case "light":
      return null;
  }
}

/**
 * A model being placed (an asset dragged over the viewport, as Unreal previews
 * a drag from the Content Browser): a ghost of the model where the new entity
 * would go, with a bounds box standing in until the model has loaded. It
 * belongs to the renderer, like the gizmo: not a proxy, never picked, and
 * nothing in the world changes until the drop.
 */
export interface PlacementPreviewDesc {
  /** The model's content path. */
  readonly asset: string;
  /** Where the new entity would go: its world matrix. */
  readonly world: Mat4;
  /** The model's bounds in its own space (the asset catalog's), for the stand-in; null if unknown. */
  readonly bounds: Aabb | null;
}

/**
 * A camera's view drawn into a corner of the viewport (Unity's Camera
 * Preview): what the camera sees, without the editor's gizmos, grid or
 * bounds. `rect` is in viewport CSS pixels from the top left.
 */
export interface CameraPreviewDesc {
  readonly camera: CameraDesc;
  readonly rect: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
}
